import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import React from 'react';

import type { ApiNotification } from '../../schemas/notification.schema';

import { useSocketStore } from '../../store/useSocketStore';
import { queryKeys } from '../../queryKeys';

export function useNotificationSocket() {
  const { socket } = useSocketStore();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!socket) return;
    const playNotificationSound = () => {
      const audio = new Audio('/NotificationAudio.mp3');
      audio.volume = 0.6;
      audio.play().catch(() => {});
    };

    const handleNotification = (notification: ApiNotification) => {
      queryClient.setQueryData<number>(
        queryKeys.notifications.unreadCount().queryKey,
        (prev) => (prev ?? 0) + 1,
      );

      queryClient.invalidateQueries({
        exact: false,
        queryKey: queryKeys.notifications.fetchInfiniteNotifications._def,
        refetchType: 'all',
      });

      enqueueSnackbar(notification.title, {
        autoHideDuration: 10_000,
        body: notification.body,
        data: notification.data,
        notificationType: notification.type,
        variant: 'notification',
      });
      playNotificationSound();
    };

    const handleTemplateArchived = () => {
      queryClient.invalidateQueries({
        exact: false,
        queryKey:
          queryKeys.notificationCenter.fetchNotificationCenterTemplates._def,
      });
    };

    socket.on('notification:new', handleNotification);
    socket.on('notification-template:archived', handleTemplateArchived);

    return () => {
      socket.off('notification:new', handleNotification);
      socket.off('notification-template:archived', handleTemplateArchived);
    };
  }, [socket, queryClient]);
}
